import { DT } from "./constants";
import { cos, sin, sqrt } from "./math";
import type { CarParams } from "./types";
import type { TrackModel, TrackSample } from "./track";
import { crashReset, type VehicleState } from "./vehicle";

/** Ticks without contact before the next touch counts as a new scrape. */
const SCRAPE_GAP_TICKS = 30;

/**
 * Keep the car between the walls. The car is a rectangle; its lateral extent against the track
 * normal is compared with the half-width. `p` is the track sample at the car's current position.
 *
 * A slow or shallow touch is a scrape: pushed out, bounced a little, speed and yaw rate bled off.
 * A hit with a wall-normal speed above the crash threshold resets the car to the centre-line.
 */
export function resolveWalls(
  s: VehicleState,
  car: CarParams,
  track: TrackModel,
  p: TrackSample,
): void {
  const wall = track.tuning.wall;
  const sy = sin(s.yaw);
  const cy = cos(s.yaw);

  // Track normal pointing left of travel, and how the car's axes project onto it.
  const nx = p.tz;
  const nz = -p.tx;
  const fn = sy * nx + cy * nz;
  const ln = cy * nx - sy * nz;
  const extent = 0.5 * car.length * Math.abs(fn) + 0.5 * car.width * Math.abs(ln);

  const penLeft = p.lateral + extent - p.halfWidth;
  const penRight = -p.lateral + extent - p.halfWidth;
  let pen: number;
  let inx: number;
  let inz: number;
  if (penLeft > 0 && penLeft >= penRight) {
    pen = penLeft;
    inx = -nx;
    inz = -nz;
  } else if (penRight > 0) {
    pen = penRight;
    inx = nx;
    inz = nz;
  } else {
    s.wallContact = false;
    return;
  }

  s.x += inx * pen;
  s.z += inz * pen;
  s.lateral += (inx * nx + inz * nz) * pen;

  // World-frame velocity: forward is (sin, cos), left is (cos, -sin).
  let wvx = s.vx * sy + s.vy * cy;
  let wvz = s.vx * cy - s.vy * sy;
  const vn = wvx * inx + wvz * inz;

  if (vn >= 0) {
    // Already moving away: pushed clear, nothing else to do.
    s.wallContact = true;
    s.lastContactTick = s.tick;
    return;
  }

  const impact = -vn;
  if (impact >= wall.crashNormalSpeed) {
    crashReset(s, track);
    s.wallContact = true;
    s.lastContactTick = s.tick;
    return;
  }

  if (s.tick - s.lastContactTick > SCRAPE_GAP_TICKS) s.scrapes += 1;
  s.wallContact = true;
  s.lastContactTick = s.tick;

  // Bounce off, then bleed the along-wall speed.
  wvx -= (1 + wall.restitution) * vn * inx;
  wvz -= (1 + wall.restitution) * vn * inz;
  const speed = sqrt(wvx * wvx + wvz * wvz);
  if (speed > 0) {
    const scale = Math.max(0, speed - wall.scrapeDecel * DT - wall.scrapeLoss * impact) / speed;
    wvx *= scale;
    wvz *= scale;
  }
  s.vx = wvx * sy + wvz * cy;
  s.vy = wvx * cy - wvz * sy;
  s.yawRate *= 1 - wall.yawDamping;
}
